'use client'

import { HotelList } from './hotel-list'
import { type FooterColumn, FooterNav } from './footer-nav'
import Section from '~/components/ui/section'
import { FooterBottom } from './footer-bottom'
import { Divider } from '~/components/ui/divider'
import { search } from '~/lib/api'
import { STATUS } from '~/data/language'
import type { SiteSettingResponse } from '~/types/site-settings'
import { useLocale } from '~/contexts/LocaleContext'
import { useQuery } from '@tanstack/react-query'
import { getLocalizedHref } from '~/lib/get-localized-href'

interface FooterProps {
  locale?: string
}

export async function FooterServer({ locale = 'vi' }: FooterProps) {
  const [footerPages, siteSettings, socialLinks, hotels] = await Promise.all([
    search('page', {
      filter: {
        status: { _eq: STATUS.PUBLISHED },
        show_in_footer: { _eq: true },
      },
      fields: [
        'id',
        'footer_group',
        'sort_menu',
        'translations.title',
        'translations.slug',
        'translations.languages_code',
        'parent_id.translations.title',
        'parent_id.translations.slug',
        'parent_id.translations.languages_code',
      ],
      sort: ['footer_group', 'sort_menu'],
    }) as unknown as Promise<any[]>,
    search('site_setting', {
      filter: { status: { _eq: STATUS.PUBLISHED } },
      fields: ['*', 'translations.*'],
    }) as unknown as Promise<SiteSettingResponse>,
    search('footer_social', {
      fields: ['id', 'url', 'icon_svg'],
      sort: ['sort_order'],
    }) as unknown as Promise<any[]>,
    search('hotel', {
      fields: ['logo', 'translations.name'],
      sort: ['order'],
    }) as unknown as Promise<any[]>,
  ])

  // Nhóm các pages theo footer_group
  const groupedPages = footerPages.reduce((acc: Record<string, any[]>, page: any) => {
    const group = page.footer_group || 'other'
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group].push(page)
    return acc
  }, {})

  // Chuyển đổi sang format FooterColumn để tương thích với FooterNav
  const footerColumns = Object.entries(groupedPages).map(([groupKey, pages]) => {
    const groupTitles = {
      about: {
        vi: 'Về Mandala Hospitality',
        en: 'About Mandala Hospitality',
        zh: '关于我们',
        ko: '회사 소개',
        ru: 'О Mandala Hospitality',
        sort: 1,
        isShow: true,
      },
      terms: {
        vi: 'Điều khoản',
        en: 'Terms',
        zh: '条款',
        ko: '약관',
        ru: 'Условия',
        sort: 2,
        isShow: true,
      },
      security: {
        vi: 'An Toàn Trật Tự',
        en: 'Security',
        zh: '安全',
        ko: '보안',
        ru: 'Безопасность и порядок',
        sort: 3,
      },
      fire_protection: {
        vi: 'Phòng Cháy Chữa Cháy',
        en: 'Fire Protection',
        zh: '消防',
        ko: '화재 예방',
        ru: 'Пожарная безопасность',
        sort: 4,
      },
      other: { vi: 'Khác', en: 'Other', zh: '其他', ko: '기타', ru: 'Прочее', sort: 5 },
    }

    // Lấy title theo locale hiện tại
    const groupTitle =
      groupTitles[groupKey as keyof typeof groupTitles]?.[
        locale as keyof (typeof groupTitles)['about']
      ] ||
      groupTitles[groupKey as keyof typeof groupTitles]?.vi ||
      groupKey

    return {
      id: groupKey,
      group: groupKey,
      sort: groupTitles[groupKey as keyof typeof groupTitles]?.sort,
      translations: [{ title: groupTitle! }],
      pages: (pages as any[])
        .sort((a, b) => (a.sort_menu || 0) - (b.sort_menu || 0))
        .map((page: any) => ({
          id: page.id,
          translations: page.translations.map((trans: any) => ({
            label: trans.title,
            url: (() => {
              const parentTrans = page.parent_id?.translations?.find(
                (pt: any) => pt.languages_code === trans.languages_code
              )
              const baseUrl = parentTrans?.slug
                ? `/${parentTrans.slug}/${trans.slug}`
                : `/${trans.slug}`
              return getLocalizedHref(baseUrl, trans.languages_code)
            })(),
            languages_code: trans.languages_code,
          })),
          sort_menu: page.sort_menu,
        })),
    } as FooterColumn
  })

  return {
    footerColumns,
    siteSettings,
    socialLinks,
    hotels,
  }
}

export function Footer() {
  const { locale } = useLocale()

  const { data, isLoading } = useQuery({
    queryKey: ['footer-data', locale],
    queryFn: () => FooterServer({ locale }),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading || !data) {
    return (
      <Section as="footer" id="footer" className="mb-[44px] bg-[#2E2F32] md:mb-0">
        <div className="mx-auto px-0">
          <div className="h-20 animate-pulse bg-gray-300"></div>
        </div>
      </Section>
    )
  }

  const { footerColumns, siteSettings, socialLinks, hotels } = data
  const siteTranslation =
    siteSettings.translations.find((tr: any) => tr.languages_code === locale) ||
    siteSettings.translations.find((tr: any) => tr.languages_code === 'vi') ||
    siteSettings.translations[0]

  return (
    <Section as="footer" id="footer" className="mb-[44px] bg-[#2E2F32] md:mb-0">
      <div className="mx-auto px-4 md:px-10">
        <HotelList list={hotels} />
        <Divider className="hidden md:block" />
        <FooterNav
          columns={footerColumns}
          contactInfo={{
            email: siteSettings.email,
            phone: siteSettings.phone,
            address: siteTranslation?.address,
            name: siteTranslation?.name,
          }}
          socialLinks={socialLinks}
        />
        <Divider className="mb-12" />
        <FooterBottom footer_text_bottom={siteTranslation?.footer_text_bottom} />
      </div>
    </Section>
  )
}
